import {
  FinancialPOADocumentContextResponseSchema,
  FinancialPOAGenerationResponseSchema,
  SharePreviewResponseSchema,
  ShareResponseSchema,
  type FinancialPOADocumentContextResponse,
  type FinancialPOAGenerationResponse,
  type FinancialPOAIntake,
  type SharePreviewResponse,
  type ShareRequest,
  type ShareResponse,
} from '@bedside-docs/contracts';

export type CreateFinancialPOAError =
  | { type: 'invalid_input'; issues: string[] }
  | { type: 'forbidden' }
  | { type: 'rate_limited' }
  | { type: 'server_error'; message?: string };

export type FetchFinancialPOADocumentContextError =
  | { type: 'not_found' }
  | { type: 'server_error'; message?: string };

const trimTrailingSlashes = (value: string) => value.replace(/\/+$/, '');

const DEFAULT_PRODUCTION_API_BASE_URL = 'https://api.bedsidedocs.com';
const DEFAULT_DEVELOPMENT_API_BASE_URL = 'http://localhost:3001';

const resolveBaseUrl = () => {
  const raw = import.meta.env.VITE_API_BASE_URL;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed.length > 0) {
      return trimTrailingSlashes(trimmed);
    }
  }

  const fallback = import.meta.env.PROD
    ? DEFAULT_PRODUCTION_API_BASE_URL
    : DEFAULT_DEVELOPMENT_API_BASE_URL;

  return trimTrailingSlashes(fallback);
};

const coerceIssues = (body: unknown): string[] => {
  if (!body || typeof body !== 'object') {
    return ['Something was wrong with the submitted information.'];
  }

  const candidate = body as Record<string, unknown>;
  const issues: string[] = [];

  if (Array.isArray(candidate.issues)) {
    for (const entry of candidate.issues) {
      if (typeof entry === 'string') {
        issues.push(entry);
      } else if (entry && typeof entry === 'object' && 'message' in entry) {
        const message = (entry as { message?: unknown }).message;
        if (typeof message === 'string' && message.trim().length > 0) {
          issues.push(message.trim());
        }
      }
    }
  }

  if (issues.length === 0 && typeof candidate.message === 'string') {
    const trimmed = candidate.message.trim();
    if (trimmed) {
      issues.push(trimmed);
    }
  }

  if (issues.length === 0) {
    issues.push('Please review the form and try again.');
  }

  return issues;
};

const coerceServerMessage = (body: unknown) => {
  if (!body || typeof body !== 'object') {
    return undefined;
  }

  const message = (body as { message?: unknown }).message;
  return typeof message === 'string' ? message : undefined;
};

const readJson = async (response: Response) => {
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.toLowerCase().includes('application/json')) {
    return null;
  }

  try {
    return await response.json();
  } catch (error) {
    console.warn('Failed to parse JSON response', error);
    return null;
  }
};

export const isCreateFinancialPOAError = (
  error: unknown,
): error is CreateFinancialPOAError =>
  Boolean(
    error &&
      typeof error === 'object' &&
      'type' in error &&
      typeof (error as { type?: unknown }).type === 'string',
  );

export const isFetchFinancialPOADocumentContextError = (
  error: unknown,
): error is FetchFinancialPOADocumentContextError => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const type = (error as { type?: unknown }).type;
  return type === 'not_found' || type === 'server_error';
};

export const createFinancialPOA = async (
  payload: FinancialPOAIntake,
): Promise<FinancialPOAGenerationResponse> => {
  const baseUrl = resolveBaseUrl();
  const url = `${baseUrl}/v1/poa/financial`;

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : undefined;
    throw { type: 'server_error', message } satisfies CreateFinancialPOAError;
  }

  const body = await readJson(response);

  if (response.ok) {
    const parsed = FinancialPOAGenerationResponseSchema.safeParse(body);
    if (parsed.success) {
      return parsed.data;
    }

    if (body && typeof body === 'object') {
      const inner = (body as { data?: unknown }).data;
      const nested = FinancialPOAGenerationResponseSchema.safeParse(inner);
      if (nested.success) {
        return nested.data;
      }
    }

    throw {
      type: 'server_error',
      message: 'The server response was missing document details.',
    } satisfies CreateFinancialPOAError;
  }

  if (response.status === 400) {
    throw {
      type: 'invalid_input',
      issues: coerceIssues(body),
    } satisfies CreateFinancialPOAError;
  }

  if (response.status === 403) {
    throw { type: 'forbidden' } satisfies CreateFinancialPOAError;
  }

  if (response.status === 429) {
    throw { type: 'rate_limited' } satisfies CreateFinancialPOAError;
  }

  throw {
    type: 'server_error',
    message: coerceServerMessage(body),
  } satisfies CreateFinancialPOAError;
};

const createIdempotencyKey = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  return `share-${Math.random().toString(36).slice(2)}`;
};

export type SendSharesPayload = Pick<ShareRequest, 'docId' | 'url' | 'recipients' | 'message'>;

export type SendSharesResult = ShareResponse;

export type SharePreviewPayload = SendSharesPayload;

export type SharePreviewResult = SharePreviewResponse;

export const sendShares = async (
  payload: SendSharesPayload,
): Promise<SendSharesResult> => {
  const baseUrl = resolveBaseUrl();
  const url = `${baseUrl}/v1/shares`;

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Idempotency-Key': createIdempotencyKey(),
      },
      body: JSON.stringify(payload),
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : undefined;
    throw new Error(message ?? 'Unable to contact the server.');
  }

  const body = await readJson(response);

  if (response.ok) {
    const parsed = ShareResponseSchema.safeParse(body);
    if (parsed.success) {
      return parsed.data;
    }

    const nested =
      body && typeof body === 'object' && 'data' in body
        ? ShareResponseSchema.safeParse((body as { data?: unknown }).data)
        : null;

    if (nested && nested.success) {
      return nested.data;
    }

    throw new Error('The server response was missing share details.');
  }

  if (response.status === 400 && body && typeof body === 'object') {
    const issues = coerceIssues(body);
    throw new Error(issues.join(' '));
  }

  if (response.status === 429) {
    const message = coerceServerMessage(body);
    throw new Error(message ?? 'Too many share requests. Please try later.');
  }

  const message = coerceServerMessage(body);
  throw new Error(message ?? 'Failed to send document shares.');
};

export const previewShareEmail = async (
  payload: SharePreviewPayload,
): Promise<SharePreviewResult> => {
  const baseUrl = resolveBaseUrl();
  const url = `${baseUrl}/v1/shares/preview`;

  let response: Response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : undefined;
    throw new Error(message ?? 'Unable to load the email preview.');
  }

  const body = await readJson(response);

  if (response.ok) {
    const parsed = SharePreviewResponseSchema.safeParse(body);
    if (parsed.success) {
      return parsed.data;
    }

    const nested =
      body && typeof body === 'object' && 'data' in body
        ? SharePreviewResponseSchema.safeParse((body as { data?: unknown }).data)
        : null;

    if (nested && nested.success) {
      return nested.data;
    }

    throw new Error('The server response was missing preview details.');
  }

  if (response.status === 400) {
    const issues = coerceIssues(body);
    throw new Error(issues.join(' '));
  }

  const message = coerceServerMessage(body);
  throw new Error(message ?? 'Failed to load the email preview.');
};

export const getFinancialPOADocumentContext = async (
  docId: string,
): Promise<FinancialPOADocumentContextResponse> => {
  const baseUrl = resolveBaseUrl();
  const url = `${baseUrl}/v1/poa/financial/${encodeURIComponent(docId)}`;

  let response: Response;
  try {
    response = await fetch(url, { method: 'GET' });
  } catch (error) {
    const message = error instanceof Error ? error.message : undefined;
    throw {
      type: 'server_error',
      message,
    } satisfies FetchFinancialPOADocumentContextError;
  }

  const body = await readJson(response);

  if (response.ok) {
    const parsed = FinancialPOADocumentContextResponseSchema.safeParse(body);
    if (parsed.success) {
      return parsed.data;
    }

    if (body && typeof body === 'object') {
      const nested = FinancialPOADocumentContextResponseSchema.safeParse(
        (body as { data?: unknown }).data,
      );
      if (nested.success) {
        return nested.data;
      }
    }

    throw {
      type: 'server_error',
      message: 'The server response was missing document details.',
    } satisfies FetchFinancialPOADocumentContextError;
  }

  if (response.status === 404) {
    throw { type: 'not_found' } satisfies FetchFinancialPOADocumentContextError;
  }

  throw {
    type: 'server_error',
    message: coerceServerMessage(body),
  } satisfies FetchFinancialPOADocumentContextError;
};
